import React, { useEffect, useState } from "react";
import { Button, Spin, Tag } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { notification } from "antd";
import moment from "moment";

import SmartRtpLayout from "./components/SmartRtpLayout";
import SmartRtpCards from "./components/SmartRtpCards";
import { getReportDetails } from "../../../controllers/V1/smartRtp/smartRtpController";

function formatDate(value) {
  return value ? moment(value).format("DD-MM-YYYY hh:mm A") : "-";
}

function num(value) {
  const n = Number(value || 0);
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

export default function SmartRtpReportDetailsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const loadDetails = async () => {
    if (!editId) {
      setLoading(false);
      setReport(null);
      return;
    }
    setLoading(true);
    const res = await getReportDetails({ editId });
    if (res.status === true) {
      setReport(res.result);
    } else {
      setReport(null);
      notification.error({
        message: "Error",
        description: res.message || "Report not found.",
        icon: <InfoCircleOutlined style={{ color: "red" }} />,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = "Crown King || Smart RTP Report Details";
    loadDetails();
  }, [editId]);

  const totals = report?.totals || {};
  const metrics = report?.metrics || {};
  const games = Array.isArray(metrics.games) ? metrics.games : [];
  const pools = Array.isArray(metrics.pools) ? metrics.pools : [];

  const cards = [
    { key: "orders", label: "Total Orders", value: totals.orders || metrics.total_orders || 0 },
    { key: "stake", label: "Total Sales (Stake)", value: totals.stake || metrics.total_stake || 0 },
    { key: "rewards", label: "Total Prize Distributed", value: totals.rewards || metrics.total_rewards || 0 },
    { key: "rtp", label: "Actual RTP %", value: totals.actual_rtp || metrics.actual_rtp || 0 },
    { key: "target", label: "Target RTP %", value: totals.target_rtp || metrics.target_rtp || 0 },
    { key: "variance", label: "RTP Variance", value: totals.variance ?? metrics.variance ?? 0 },
    { key: "tickets", label: "Total Tickets", value: metrics.total_tickets || 0 },
    { key: "wins", label: "Win Orders", value: metrics.win_orders || 0 },
    { key: "buyers", label: "Unique Buyers", value: metrics.unique_buyers || 0 },
    { key: "pos", label: "Unique POS", value: metrics.unique_pos || 0 },
    { key: "commission", label: "Commission", value: metrics.total_commission || 0 },
    { key: "outflow", label: "Pool Outflow", value: metrics.outflow || metrics.total_rewards || 0 },
  ];

  return (
    <SmartRtpLayout
      title="Smart RTP Report Details"
      activeKey="reports"
      headerVariant="simple"
      onRefresh={loadDetails}
      actions={
        <Link to="/smart-rtp/reports">
          <Button type="secondary">
            <ArrowLeftOutlined /> Back
          </Button>
        </Link>
      }
    >
      {loading ? (
        <div className="card shadow mb-4 p-4 text-center">
          <Spin />
        </div>
      ) : !report ? (
        <div className="card shadow mb-4 p-4">Report not found.</div>
      ) : (
        <>
          <div className="card shadow mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">{report.title || report.report_key}</h5>
                  <Tag color="blue">{report.report_type}</Tag>
                  <Tag color={report.status === "Ready" ? "green" : "orange"}>{report.status}</Tag>
                  <Tag>{report.generated_by || "-"}</Tag>
                </div>
                <Button onClick={() => navigate("/smart-rtp/reports")}>Back to Reports</Button>
              </div>
              <div className="row">
                <div className="col-md-4 mb-2"><strong>Report Key:</strong> {report.report_key || "-"}</div>
                <div className="col-md-4 mb-2"><strong>Period Start:</strong> {formatDate(report.period_start)}</div>
                <div className="col-md-4 mb-2"><strong>Period End:</strong> {formatDate(report.period_end)}</div>
                <div className="col-md-4 mb-2"><strong>Generated At:</strong> {formatDate(report.generated_at || report.createdAt)}</div>
                <div className="col-md-4 mb-2"><strong>Game Id:</strong> {report.game_id || "Network-wide"}</div>
                <div className="col-md-4 mb-2"><strong>Pool Count:</strong> {metrics.count || pools.length || 0}</div>
              </div>
            </div>
          </div>

          <SmartRtpCards cards={cards} />

          {games.length > 0 ? (
            <div className="card shadow mb-4">
              <div className="card-body pt-0">
                <h6 className="pt-3 mb-3">Game-wise breakdown</h6>
                <div className="table-responsive">
                  <table className="table table-bordered" width="100%">
                    <thead>
                      <tr>
                        <th>Sl.</th>
                        <th>Game</th>
                        <th>Orders</th>
                        <th>Sales (Stake)</th>
                        <th>Wins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map((row, index) => (
                        <tr key={row._id || index}>
                          <td>{index + 1}</td>
                          <td>{row.game_code || row._id || "-"}</td>
                          <td>{num(row.orders)}</td>
                          <td>{num(row.stake)}</td>
                          <td>{num(row.wins)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}

          {pools.length > 0 ? (
            <div className="card shadow mb-4">
              <div className="card-body pt-0">
                <h6 className="pt-3 mb-3">Pool reconciliation</h6>
                <div className="table-responsive">
                  <table className="table table-bordered" width="100%">
                    <thead>
                      <tr>
                        <th>Sl.</th>
                        <th>Pool Id</th>
                        <th>Prize Distributed</th>
                        <th>Payout Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pools.map((row, index) => (
                        <tr key={row._id || index}>
                          <td>{index + 1}</td>
                          <td>{row._id || "-"}</td>
                          <td>{num(row.outflow)}</td>
                          <td>{num(row.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </SmartRtpLayout>
  );
}
